import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'

import type { StepUpCredential } from '@aka/ui'

import * as authApi from '@/api/auth'
import type { AuthToken, InviteAcceptance, MeUser, MfaSetup } from '@/api/auth'
import { ApiError, isRefusal } from '@/api/client'
import { clearToken, setToken, token, tokenStage } from '@/api/session'
import { disconnectEcho } from '@/live/echo'
import { identify } from '@/observability'

export type MfaStage = 'setup_required' | 'verify_required' | 'verified'

/**
 * Who is signed in, and what they may do.
 *
 * There is no refresh endpoint: the API issues a 12-hour sliding token and the
 * panel proves it is still good by calling `/auth/me` on load. That call is
 * also how permissions and the effective timezone reach the rest of the app,
 * so it runs before the first route resolves.
 */
export const useAuthStore = defineStore('auth', () => {
  const user = ref<MeUser | null>(null)
  const mfaStage = ref<MfaStage | null>(null)
  /** False until restore() has settled, so a guard never redirects too early. */
  const ready = ref(false)
  /**
   * Raised when `/auth/me` answered for a tenant account. Login already refuses
   * one, so this only catches a token that arrived another way: an old one in
   * storage, or a tenant invitation accepted on this origin. The sign-in page
   * reads it once and lowers it.
   */
  const realmRefused = ref(false)

  const isAuthenticated = computed(() => token.value !== null && user.value !== null)
  const permissions = computed(() => new Set(user.value?.permissions ?? []))
  /**
   * The channel live updates arrive on (MRS 6.15): every operator joins the
   * one operator channel, and Echo adds the `private-` prefix. It never names
   * a tenant: the token that views a tenant goes to the console tab the
   * hand-off opens, and that tab joins the tenant channel (D127). Null with
   * no session, and then nothing subscribes.
   */
  const liveChannel = computed(() => (user.value === null ? null : 'operator'))
  /**
   * A coarse clock, so `stepUpFresh` lapses on its own rather than only when
   * the user object changes. Fifteen seconds is fine: the window is five
   * minutes, and the API is the judge either way.
   */
  const now = ref(Date.now())
  setInterval(() => {
    now.value = Date.now()
  }, 15_000)
  /** Whether a destructive call would pass the API's five-minute step-up check right now. */
  const stepUpFresh = computed(() => {
    const until = user.value?.step_up_valid_until
    return until != null && Date.parse(until) > now.value
  })

  function can(permission: string): boolean {
    return permissions.value.has(permission)
  }

  // The API client drops a rejected token on its own (see api/index.ts);
  // everything that hung off it goes here, so no stale user survives.
  watch(token, (value) => {
    if (value === null && user.value !== null) forget()
  })

  function forget(): void {
    user.value = null
    mfaStage.value = null
    clearToken()
    identify(null)
    // The socket was authorised with the token that just went (MRS 6.15):
    // closed here, so every way out of a session closes it.
    disconnectEcho()
  }

  async function loadUser(): Promise<void> {
    const me = await authApi.fetchMe()
    // An operator panel session is an operator or nothing. The token is
    // dropped here rather than thrown at the page: the caller carries on to
    // the shell, the guard finds no session and sends it to sign in, and the
    // sign-in page explains.
    if (me.realm !== 'operator') {
      forget()
      realmRefused.value = true
      return
    }
    user.value = me
    mfaStage.value = 'verified'
    identify({ id: me.id, email: me.email })
  }

  function acknowledgeRealm(): void {
    realmRefused.value = false
  }

  /** Keeps a token the API just issued and finishes loading who it belongs to. */
  async function adopt(issued: { token: string; mfa: MfaStage }): Promise<void> {
    setToken(issued.token, issued.mfa)
    mfaStage.value = issued.mfa
    await loadUser()
  }

  async function restore(): Promise<void> {
    if (token.value === null) {
      ready.value = true
      return
    }
    // A reload in the middle of MFA: the token cannot open the console yet and
    // `/auth/me` would only say so. Put the person back on the page they left.
    if (tokenStage.value === 'setup_required' || tokenStage.value === 'verify_required') {
      mfaStage.value = tokenStage.value
      ready.value = true
      return
    }
    try {
      await loadUser()
    } catch {
      // Expired, revoked, or issued before an MFA step. Either way this token
      // cannot open the console, so drop it rather than retry on every route.
      forget()
    } finally {
      ready.value = true
    }
  }

  async function signIn(email: string, password: string): Promise<MfaStage> {
    const issued = await authApi.login(email, password)
    setToken(issued.token, issued.mfa)
    mfaStage.value = issued.mfa
    // setup_required and verify_required tokens may only call the MFA
    // endpoints, so asking for the user now would just earn a 403.
    if (issued.mfa === 'verified') {
      await loadUser()
    }
    return issued.mfa
  }

  /**
   * An accepted invitation earns a token that may only reach the MFA
   * endpoints, exactly like a fresh sign-in, so the user is loaded only once
   * the API says the token is fully verified.
   */
  async function acceptInvite(body: InviteAcceptance): Promise<MfaStage> {
    const issued = await authApi.acceptInvite(body)
    setToken(issued.token, issued.mfa)
    mfaStage.value = issued.mfa
    if (issued.mfa === 'verified') await loadUser()
    return issued.mfa
  }

  /**
   * Finishes a sign-in with a code from the authenticator or a recovery
   * code. A code accepted once already swapped the token for a full one; if
   * only the `/auth/me` after it failed, the page never moved on and every
   * retry earns 403 `mfa_token_required` (saas-admin #48). For a stored
   * stage of `verified` that refusal means the person is in, so the user is
   * loaded and the page carries on; a load that fails then drops the token,
   * since nothing else can. A real pre-MFA token never gets this code, so
   * with any other stage the refusal reaches the page unchanged.
   */
  async function finishMfa(issue: () => Promise<AuthToken>): Promise<void> {
    let issued: AuthToken
    try {
      issued = await issue()
    } catch (error) {
      if (!isRefusal(error, 403, 'mfa_token_required') || tokenStage.value !== 'verified') {
        throw error
      }
      try {
        await loadUser()
      } catch (loadError) {
        forget()
        throw loadError
      }
      return
    }
    await adopt(issued)
  }

  async function confirmMfa(code: string): Promise<void> {
    await finishMfa(() => authApi.verifyMfa(code))
  }

  /** A recovery code stands in for the authenticator once, then is spent. */
  async function recover(code: string): Promise<void> {
    await finishMfa(() => authApi.recover(code))
  }

  /**
   * First-time enrolment. Call once per screen: every call replaces the
   * unconfirmed secret, so a second one would orphan a QR already scanned.
   */
  async function beginMfaSetup(): Promise<MfaSetup> {
    return authApi.mfaSetup()
  }

  /**
   * Proves the authenticator works and returns the recovery codes. They are
   * returned rather than stored: the screen shows them once and they are gone.
   */
  async function completeMfaSetup(code: string): Promise<string[]> {
    const confirmed = await authApi.mfaConfirm(code)
    await adopt(confirmed)
    return confirmed.recovery_codes
  }

  /**
   * Re-authenticates for the step-up dialog. Answers whether the credential
   * was accepted; a wrong one is an answer, not an error, which is the shape
   * the library's dialog expects from its `verify` callback.
   */
  async function stepUp(credential: StepUpCredential): Promise<boolean> {
    try {
      const opened = await authApi.stepUp(
        credential.kind === 'password'
          ? { password: credential.value }
          : { code: credential.value },
      )
      if (user.value !== null) {
        user.value = { ...user.value, step_up_valid_until: opened.step_up_valid_until }
      }
      now.value = Date.now()
      return true
    } catch (error) {
      if (error instanceof ApiError && (error.status === 401 || error.status === 422)) return false
      throw error
    }
  }

  /**
   * Saves the signed-in operator's own name or profile time zone (null hands
   * the zone back to the platform's). The API answers with the whole user, so
   * the account strip and every rendered time follow at once, with no reload.
   */
  async function updateProfile(changes: {
    name?: string
    timezone?: string | null
  }): Promise<void> {
    if (user.value === null) throw new Error('updateProfile needs a signed-in user.')
    user.value = await authApi.updateMe(changes)
  }

  /**
   * Throws away a token that never finished multi-factor authentication.
   *
   * Without this a user who abandons the code screen is stuck: the guard sends
   * them back to it on every navigation, and the half-authenticated token sits
   * in storage across reloads with no way to discard it. No revoke call,
   * because a pre-MFA token may only reach the MFA endpoints.
   */
  function abandonSignIn(): void {
    forget()
  }

  async function signOut(): Promise<void> {
    try {
      await authApi.logout()
    } catch {
      // A token that cannot be revoked is still a token this browser must
      // stop using. Server-side expiry catches the rest.
    } finally {
      forget()
    }
  }

  return {
    user,
    mfaStage,
    ready,
    realmRefused,
    acknowledgeRealm,
    isAuthenticated,
    liveChannel,
    stepUpFresh,
    can,
    restore,
    signIn,
    acceptInvite,
    confirmMfa,
    recover,
    beginMfaSetup,
    completeMfaSetup,
    stepUp,
    updateProfile,
    abandonSignIn,
    signOut,
  }
})
